import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
    Aangifte,
    BokkzIbDataService,
    IncomeTaxEntity,
    Onderneming,
    Ondernemingsvorm, ResultaatSamenwerking
} from '@snelstart-ventures/fiscaal/domain-logic';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  merge,
  Observable,
  shareReplay,
  Subscription,
  take,
  tap
} from 'rxjs';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-samenwerkingsverband',
  templateUrl: './samenwerkingsverband.component.html',
  styleUrls: ['./samenwerkingsverband.component.scss'],
  imports: [
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class SamenwerkingsverbandComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public indexOnderneming = 0;
  public heeftSamenwerkingsverband = false;
  public dialogVisible = false;
  public isOndernemer = false;
  public isPartner = false;
  public isDefAangifte = false;
  public index = 0;

  public formGroup = new FormGroup({
    resultaatSamenwerking: new FormControl<number | null>(0),
    aandeelBelastingplichtigeInKapitaalVolgensContract: new FormControl<number | null>(0),
    aandeelBelastingplichtigeInKapitaalOverig: new FormControl<number | 0>(0),
    aandeelBelastingplichtigeInBeginVermogen: new FormControl<number | null>(0),
    vergoedingVoorArbeidBelastingplichtige: new FormControl<number | null>(0),
    overigeVergoedingenBelastingplichtige: new FormControl<number | null>(0),
    aandeelRestantWinstBelastingplichtige: new FormControl<number | null>(0),
    totaalResultaat: new FormControl<number | null>(0),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      tap((data) => {
        this.isDefAangifte = data.taxType === 'DA';
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: Aangifte): void {
    this.formGroup.reset({}, {emitEvent: false});
    this.isOndernemer = false;
    this.heeftSamenwerkingsverband = false;
      if (details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)) {
          this.isOndernemer = true;
          this.heeftSamenwerkingsverband =
              details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.ondernemingsvorm !==
              Ondernemingsvorm.EENMANSZAAK;
          const resultaat = (details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.resultaatSamenwerking ??
              {}) as Partial<ResultaatSamenwerking>;
          this.formGroup.patchValue(
              {
                  ...resultaat,
                  aandeelBelastingplichtigeInKapitaalOverig: resultaat.aandeelBelastingplichtigeInKapitaalOverig ?? 0, // nooit null
              },
              { emitEvent: false },
          );
      }
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const {
      vergoedingVoorArbeidBelastingplichtige,
      overigeVergoedingenBelastingplichtige,
      aandeelRestantWinstBelastingplichtige,
    } = this.formGroup.controls;

    this.changeSubscriptions.add(
      merge(
        vergoedingVoorArbeidBelastingplichtige.valueChanges,
        overigeVergoedingenBelastingplichtige.valueChanges,
        aandeelRestantWinstBelastingplichtige.valueChanges
      ).pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => this.saveForm()),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  public berekenTotaal(): any {
    const vergoedingArbeid = this.formGroup.get('vergoedingVoorArbeidBelastingplichtige')?.value ?? 0;
    const overigeVergoedingen = this.formGroup.get('overigeVergoedingenBelastingplichtige')?.value ?? 0;
    const aandeelWinst = this.formGroup.get('aandeelRestantWinstBelastingplichtige')?.value ?? 0;
    const sum = vergoedingArbeid + overigeVergoedingen + aandeelWinst;
    this.formGroup.patchValue({totaalResultaat: sum}, {emitEvent: false});
    return sum;
  }

  saveForm(): void {
    const idx = this.indexOnderneming;
    const formValue = this.formGroup.getRawValue();
    const hasResultaatSamenwerkingValues = Object.values(formValue).some((value) => value !== null && value !== 0);

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const onderneming = existingOndernemingen[idx] || {};
        const {resultaatSamenwerking, ...ondernemingZonderResultaatSamenwerking} = onderneming;
        const updatedOnderneming: Partial<Onderneming> = {
          ...ondernemingZonderResultaatSamenwerking,
          ...(hasResultaatSamenwerkingValues ? {resultaatSamenwerking: formValue} : {}),
        };
        // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
        const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
        const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => i === idx ? cleanedOnderneming : o);
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };
      const updatedAangifte = this.isPartner
        ? {
          ...currentEntity.aangifte,
          partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
        }
        : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }
}
